import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  LinearProgress, Tab, Tabs, Typography,
} from '@mui/material'
import CheckCircleIcon         from '@mui/icons-material/CheckCircle'
import LockIcon                from '@mui/icons-material/Lock'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import FiberManualRecordIcon   from '@mui/icons-material/FiberManualRecord'
import EventIcon               from '@mui/icons-material/Event'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'
import PolishComposer from '../../components/messaging/PolishComposer'
import ImportantDatesCard from '../../components/fair/ImportantDatesCard'

// ── Types ─────────────────────────────────────────────────────────────────────

interface LinkedStudent {
  student_id:  string
  user_id:     string
  first_name:  string
  last_name:   string
  grade:       number
  school_name: string
  notify_milestones:      boolean
  notify_mentor_feedback: boolean
  notify_weekly:          boolean
}

type NotifyPref = 'notify_milestones' | 'notify_mentor_feedback' | 'notify_weekly'

interface StudentProject {
  project_id:   string
  current_step: number
  track:        string | null
}

interface Substep {
  substep_id:     string
  step_number:    number
  substep_number: number
  label:          string
  required:       boolean
  track:          string | null
}

interface SubstepStatus {
  substep_id: string
  status:     'not_started' | 'in_progress' | 'completed'
}

interface StepTarget {
  step_number: number
  target_date: string
}

interface MeetingItem {
  id:           string
  kind:         'mentor' | 'class'
  title:        string
  scheduled_at: string
  status:       string
  format:       string | null
  location:     string | null
}

interface ParentMsg {
  id:         string
  direction:  'parent_to_staff' | 'staff_to_parent'
  body:       string
  created_at: string
}

// ── Constants ─────────────────────────────────────────────────────────────────

// Canonical 8-step process, track-aware — must match the student-facing labels
// in StepRoadmap / step / dashboard so the parent sees the same names.
const STEP_LABELS_SCIENCE: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Ask a Research Question',
  3: 'Gather Background Information',
  4: 'Formulate a Hypothesis',
  5: 'Design an Experiment',
  6: 'Conduct the Experiment and Record Data',
  7: 'Analyze Data and Draw Conclusions',
  8: 'Present Your Findings',
}
const STEP_LABELS_ENGINEERING: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Identify a Problem',
  3: 'Gather Background Information',
  4: 'Specify Design Requirements',
  5: 'Design a Solution',
  6: 'Develop the Solution',
  7: 'Test and Record Performance',
  8: 'Present Your Solutions',
}
const TOTAL_STEPS = 8
// Steps 1–3 are built and their substeps are finalized. Steps 4–8 are still
// being designed, so their substep breakdown is shown as a preview.
const FINALIZED_THROUGH_STEP = 3

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepRow({
  stepNumber,
  stepLabel,
  currentStep,
  substeps,
  substepStatuses,
  target,
  preliminary,
}: {
  stepNumber:      number
  stepLabel:       string
  currentStep:     number
  substeps:        Substep[]
  substepStatuses: SubstepStatus[]
  target:          StepTarget | undefined
  preliminary:     boolean
}) {
  const status = stepNumber < currentStep ? 'complete'
               : stepNumber === currentStep ? 'current'
               : 'locked'

  const statusColor = status === 'complete' ? 'success.main'
                    : status === 'current'  ? 'primary.main'
                    : 'text.disabled'

  const Icon = status === 'complete' ? CheckCircleIcon
             : status === 'locked'   ? LockIcon
             : RadioButtonUncheckedIcon

  return (
    <Box sx={{
      border:       '1px solid',
      borderColor:  status === 'current' ? 'primary.main' : status === 'complete' ? 'success.light' : 'divider',
      borderWidth:  status === 'current' ? 2 : 1,
      borderRadius: 2,
      bgcolor:      status === 'complete' ? 'rgba(76,175,80,0.04)'
                  : status === 'locked'   ? 'transparent'
                  : 'background.paper',
      opacity:      status === 'locked' ? 0.45 : 1,
      overflow:     'hidden',
    }}>
      {/* Step header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
        <Box sx={{ color: statusColor, flexShrink: 0 }}>
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            Step {stepNumber}: {stepLabel}
          </Typography>
        </Box>
        {status === 'complete' && (
          <Chip label="Complete" size="small" color="success" />
        )}
        {status === 'current' && (
          <Chip label="In Progress" size="small" color="primary" />
        )}
        {status === 'locked' && (
          <Chip label="Locked" size="small" />
        )}
      </Box>

      {/* Substeps — shown only for completed and in-progress steps (not locked
          future ones). Per-substep tracking isn't recorded yet, so appearance
          is derived from the step's status: completed steps show their substeps
          done, the current step neutral. */}
      {status !== 'locked' && substeps.length > 0 && (
        <Box sx={{ px: 2, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ pt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {substeps.map(sub => {
              const ss = substepStatuses.find(s => s.substep_id === sub.substep_id)
              const subStatus = ss?.status ?? (status === 'complete' ? 'completed' : 'not_started')
              return (
                <Box key={sub.substep_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <FiberManualRecordIcon sx={{
                    fontSize: 8,
                    flexShrink: 0,
                    color: subStatus === 'completed'  ? 'success.main'
                         : subStatus === 'in_progress' ? 'primary.main'
                         : 'text.disabled',
                  }} />
                  <Typography sx={{
                    fontSize:       '0.8rem',
                    color:          subStatus === 'completed' ? 'text.secondary' : 'text.primary',
                    textDecoration: subStatus === 'completed' ? 'line-through' : 'none',
                  }}>
                    {sub.substep_number.toFixed(1)}&nbsp; {sub.label}
                  </Typography>
                  {subStatus === 'completed' && (
                    <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main', ml: 'auto', flexShrink: 0 }} />
                  )}
                  {subStatus === 'in_progress' && (
                    <Chip label="In progress" size="small" color="primary" sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              )
            })}
          </Box>

          {preliminary && (
            <Typography sx={{ mt: 1, fontSize: '0.7rem', color: 'text.disabled', fontStyle: 'italic' }}>
              Preview — we're still refining the details of this step.
            </Typography>
          )}

          {/* Target date */}
          <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EventIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            {target ? (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Target: {formatDate(target.target_date)}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                No target date set &middot; Ask your student to add one in their portal
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ParentPortal() {
  const { session }                           = useAuth()
  const [students,       setStudents]         = useState<LinkedStudent[]>([])
  const [activeIdx,      setActiveIdx]        = useState(0)
  const [project,        setProject]          = useState<StudentProject | null>(null)
  const [substeps,       setSubsteps]         = useState<Substep[]>([])
  const [substepStatuses, setSubstepStatuses] = useState<SubstepStatus[]>([])
  const [targets,        setTargets]          = useState<StepTarget[]>([])
  const [meetings,       setMeetings]         = useState<MeetingItem[]>([])
  const [parentMsgs,     setParentMsgs]       = useState<ParentMsg[]>([])
  const [tab,            setTab]              = useState(0)
  const [loading,        setLoading]          = useState(true)

  // Load all linked students once
  useEffect(() => {
    async function loadStudents() {
      if (!session) return
      const { data: links } = await supabase
        .from('parent_student_link')
        .select('student_id, notify_milestones, notify_mentor_feedback, notify_weekly')
        .eq('parent_user_id', session.user.id)

      if (!links?.length) { setLoading(false); return }

      const studentIds = links.map(l => l.student_id)

      const { data: profiles } = await supabase
        .from('student_profile')
        .select('student_id, user_id, grade, school_name')
        .in('student_id', studentIds)

      const userIds = profiles?.map(p => p.user_id) ?? []
      const { data: users } = await supabase
        .from('users')
        .select('user_id, first_name, last_name')
        .in('user_id', userIds)

      const merged: LinkedStudent[] = (profiles ?? []).map(p => {
        const u    = users?.find(u => u.user_id === p.user_id)
        const link = links.find(l => l.student_id === p.student_id)
        return {
          student_id:  p.student_id,
          user_id:     p.user_id,
          first_name:  u?.first_name ?? '',
          last_name:   u?.last_name  ?? '',
          grade:       p.grade,
          school_name: p.school_name,
          notify_milestones:      link?.notify_milestones      ?? false,
          notify_mentor_feedback: link?.notify_mentor_feedback ?? false,
          notify_weekly:          link?.notify_weekly          ?? false,
        }
      })

      setStudents(merged)
      setLoading(false)
    }
    loadStudents()
  }, [session])

  // Load project data when the active student changes. Keyed on the student's
  // id (not the whole students array) so toggling a notification preference —
  // which updates the students array optimistically — doesn't retrigger a full
  // project reload / loading flicker.
  const activeStudentId = students[activeIdx]?.student_id
  useEffect(() => {
    if (!activeStudentId) return
    let cancelled = false
    async function loadProject() {
      setLoading(true)

      const [{ data: projects }, { data: allSubsteps }] = await Promise.all([
        supabase
          .from('project')
          .select('project_id, current_step, track')
          .eq('student_id', activeStudentId)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1),
        supabase
          .from('substep')
          .select('substep_id, step_number, substep_number, label, required, track')
          .order('substep_number'),
      ])
      if (cancelled) return

      const proj = projects?.[0] ?? null
      setProject(proj)
      setSubsteps(allSubsteps ?? [])

      if (proj) {
        const [{ data: statuses }, { data: stepTargets }] = await Promise.all([
          supabase
            .from('substep_status')
            .select('substep_id, status')
            .eq('project_id', proj.project_id),
          supabase
            .from('project_step_target')
            .select('step_number, target_date')
            .eq('project_id', proj.project_id),
        ])
        if (cancelled) return
        setSubstepStatuses(statuses ?? [])
        setTargets(stepTargets ?? [])
      } else {
        setSubstepStatuses([])
        setTargets([])
      }

      setLoading(false)
    }
    loadProject()
    return () => { cancelled = true }
  }, [activeStudentId])

  // Load scheduled meetings for the active student: 1:1 mentor meetings plus
  // any meetings for the classes they're enrolled in. Keyed on student id so it
  // doesn't reload on preference toggles.
  useEffect(() => {
    if (!activeStudentId) return
    let cancelled = false
    async function loadMeetings() {
      const [{ data: oneOnOne }, { data: enrolls }] = await Promise.all([
        supabase
          .from('meeting')
          .select('meeting_id, scheduled_at, status, mentor_id')
          .eq('student_id', activeStudentId)
          .not('scheduled_at', 'is', null)
          .neq('status', 'cancelled'),
        supabase
          .from('class_enrollment')
          .select('class_id')
          .eq('student_id', activeStudentId)
          .eq('status', 'active'),
      ])

      const classIds = (enrolls ?? []).map((e: { class_id: string }) => e.class_id)
      let classMeetings: {
        meeting_id: string; class_id: string; kind: string; scheduled_at: string
        status: string; format: string | null; location: string | null
      }[] = []
      let classNames: Record<string, string> = {}
      if (classIds.length) {
        const [{ data: cms }, { data: classes }] = await Promise.all([
          supabase
            .from('class_meeting')
            .select('meeting_id, class_id, kind, scheduled_at, status, format, location')
            .in('class_id', classIds)
            .neq('status', 'cancelled'),
          supabase.from('class').select('class_id, name').in('class_id', classIds),
        ])
        classMeetings = cms ?? []
        classNames = Object.fromEntries((classes ?? []).map((c: { class_id: string; name: string }) => [c.class_id, c.name]))
      }

      const mentorIds = [...new Set((oneOnOne ?? [])
        .map((m: { mentor_id: string | null }) => m.mentor_id)
        .filter((id): id is string => !!id))]
      let mentorNames: Record<string, string> = {}
      if (mentorIds.length) {
        const { data: ms } = await supabase
          .from('users').select('user_id, first_name, last_name').in('user_id', mentorIds)
        mentorNames = Object.fromEntries((ms ?? []).map(
          (u: { user_id: string; first_name: string | null; last_name: string | null }) =>
            [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]))
      }

      const items: MeetingItem[] = [
        ...(oneOnOne ?? []).map((m: {
          meeting_id: string; scheduled_at: string; status: string; mentor_id: string | null
        }): MeetingItem => ({
          id:           m.meeting_id,
          kind:         'mentor',
          title:        m.mentor_id && mentorNames[m.mentor_id]
                          ? `Mentor meeting with ${mentorNames[m.mentor_id]}`
                          : 'Mentor meeting',
          scheduled_at: m.scheduled_at,
          status:       m.status,
          format:       null,
          location:     null,
        })),
        ...classMeetings.map((cm): MeetingItem => ({
          id:           cm.meeting_id,
          kind:         'class',
          title:        `${classNames[cm.class_id] ?? 'Class'} — ${cm.kind === 'office_hours' ? 'Office hours' : 'Class meeting'}`,
          scheduled_at: cm.scheduled_at,
          status:       cm.status,
          format:       cm.format,
          location:     cm.location,
        })),
      ].sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())

      if (!cancelled) setMeetings(items)
    }
    loadMeetings()
    return () => { cancelled = true }
  }, [activeStudentId])

  // Parent ↔ staff message thread for the active student.
  const loadMessages = React.useCallback(async () => {
    if (!activeStudentId || !session) return
    const { data } = await supabase
      .from('parent_message')
      .select('id, direction, body, created_at')
      .eq('student_id', activeStudentId)
      .eq('parent_user_id', session.user.id)
      .order('created_at', { ascending: true })
    setParentMsgs((data ?? []) as ParentMsg[])
  }, [activeStudentId, session])

  useEffect(() => { loadMessages() }, [loadMessages])

  async function sendToMentor(text: string): Promise<string | null> {
    if (!activeStudentId) return 'No student selected.'
    const { data, error } = await supabase.functions.invoke('parent-send-message', {
      body: { student_id: activeStudentId, body: text },
    })
    if (error) return 'Could not send — please try again.'
    if (data?.error) return data.error
    await loadMessages()
    return null
  }

  // Toggle a notification preference for the active student. Optimistic —
  // the checkbox reflects the change immediately; a failed write reverts.
  async function updatePref(studentId: string, key: NotifyPref, value: boolean) {
    if (!session) return
    setStudents(prev => prev.map(s =>
      s.student_id === studentId ? { ...s, [key]: value } : s,
    ))
    const { error } = await supabase
      .from('parent_student_link')
      .update({ [key]: value })
      .eq('parent_user_id', session.user.id)
      .eq('student_id', studentId)
    if (error) {
      setStudents(prev => prev.map(s =>
        s.student_id === studentId ? { ...s, [key]: !value } : s,
      ))
    }
  }

  // Send the parent a status email right now, reflecting their checked options.
  async function sendStatusNow(studentId: string): Promise<string | null> {
    const { data, error } = await supabase.functions.invoke('notify-parent-status', {
      body: { student_id: studentId },
    })
    if (error) return 'Could not send the update. Please try again.'
    if (data?.error) return data.error
    return null
  }

  if (loading) return (
    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  )

  const student    = students[activeIdx]
  const currentStep = project?.current_step ?? 1
  const completed   = currentStep - 1
  const progress    = (completed / TOTAL_STEPS) * 100
  const STEP_LABELS = project?.track === 'engineering_design' ? STEP_LABELS_ENGINEERING : STEP_LABELS_SCIENCE
  const fullName    = `${student?.first_name ?? ''} ${student?.last_name ?? ''}`.trim() || student?.first_name || 'Student'

  return (
    <Box sx={{ p: { xs: 3, sm: 4 }, maxWidth: 820 }}>

      {/* ── Student selector row ── */}
      {students.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {students.map((s, i) => {
            const name = `${s.first_name} ${s.last_name}`.trim() || s.first_name || 'Student'
            return (
              <Chip
                key={s.student_id}
                label={name}
                onClick={() => setActiveIdx(i)}
                color={i === activeIdx ? 'primary' : 'default'}
                variant={i === activeIdx ? 'filled' : 'outlined'}
                sx={{ fontWeight: i === activeIdx ? 700 : 500 }}
              />
            )
          })}
        </Box>
      )}

      {/* ── Section tabs ── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Progress"      sx={{ fontWeight: 600 }} />
        <Tab label="Notifications" sx={{ fontWeight: 600 }} />
        <Tab label="Meetings"      sx={{ fontWeight: 600 }} />
        <Tab label="Message"       sx={{ fontWeight: 600 }} />
      </Tabs>

      {tab === 0 && (
        <>
          {/* ── Student summary ── */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
              {fullName}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Grade {student?.grade} &middot; {student?.school_name}
            </Typography>
          </Box>

          {/* ── Progress bar ── */}
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Step {currentStep} of {TOTAL_STEPS} &middot; {completed} step{completed !== 1 ? 's' : ''} complete
            </Typography>
          </Box>

          {/* ── Fair important dates — reminder layer, self-hides if no fair entry ── */}
          {project && (
            <ImportantDatesCard projectId={project.project_id} studentName={student?.first_name} />
          )}

          {/* ── Research roadmap ── */}
          <Typography variant="subtitle2" sx={{
            fontWeight: 700, mb: 1.5, color: 'text.secondary',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.72rem',
          }}>
            Research Roadmap
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(num => (
              <StepRow
                key={num}
                stepNumber={num}
                stepLabel={STEP_LABELS[num]}
                currentStep={currentStep}
                substeps={substeps.filter(s =>
                  s.step_number === num && (s.track == null || s.track === project?.track))}
                substepStatuses={substepStatuses}
                target={targets.find(t => t.step_number === num)}
                preliminary={num > FINALIZED_THROUGH_STEP}
              />
            ))}
          </Box>
        </>
      )}

      {tab === 1 && student && (
        <NotificationSettings student={student} onToggle={updatePref} onSendStatus={sendStatusNow} />
      )}

      {tab === 2 && <MeetingsTab meetings={meetings} />}

      {tab === 3 && student && (
        <MessageTab
          studentFirst={student.first_name}
          messages={parentMsgs}
          onSend={sendToMentor}
        />
      )}
    </Box>
  )
}

// ── Notification preferences ────────────────────────────────────────────────────

const NOTIFY_OPTIONS: { key: NotifyPref; title: string; description: string }[] = [
  {
    key:         'notify_milestones',
    title:       'Milestones',
    description: 'The big moments — a step is approved, the project advances, or a mentor meeting is scheduled.',
  },
  {
    key:         'notify_mentor_feedback',
    title:       'Mentor feedback',
    description: 'When a mentor sends work back with feedback, so you can gently remind your student to review it and keep going.',
  },
  {
    key:         'notify_weekly',
    title:       'Weekly summary',
    description: 'One calm email a week with a recap and where your student is compared to their schedule.',
  },
]

function NotificationSettings({
  student,
  onToggle,
  onSendStatus,
}: {
  student:      LinkedStudent
  onToggle:     (studentId: string, key: NotifyPref, value: boolean) => void
  onSendStatus: (studentId: string) => Promise<string | null>
}) {
  const name  = `${student.first_name} ${student.last_name}`.trim() || student.first_name || 'your student'
  const anyOn = student.notify_milestones || student.notify_mentor_feedback || student.notify_weekly

  const [sending, setSending] = useState(false)
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string } | null>(null)

  async function handleSend() {
    setSending(true)
    setSendMsg(null)
    const err = await onSendStatus(student.student_id)
    setSendMsg(err ? { ok: false, text: err } : { ok: true, text: 'Sent — check your inbox.' })
    setSending(false)
  }

  return (
    <Box sx={{ maxWidth: 620 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        Email notifications
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 3, lineHeight: 1.65 }}>
        Choose what you'd like to hear about for {name}. These are gentle reminders — pick as many or as
        few as you like. Every email includes where {student.first_name || 'your student'} is expected to
        be versus where they are now.
      </Typography>

      {!anyOn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're not receiving any emails right now. Turn on anything you'd find helpful below.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {NOTIFY_OPTIONS.map(opt => (
          <Box
            key={opt.key}
            sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.25,
            }}
          >
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={student[opt.key]}
                  onChange={e => onToggle(student.student_id, opt.key, e.target.checked)}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{opt.title}</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.55 }}>
                    {opt.description}
                  </Typography>
                </Box>
              }
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          onClick={handleSend}
          disabled={sending || !anyOn}
        >
          {sending ? <CircularProgress size={20} /> : 'Email me a status update now'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Sends one email to you right now, showing exactly what your chosen updates will look like.
        </Typography>
        {sendMsg && (
          <Alert severity={sendMsg.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>
            {sendMsg.text}
          </Alert>
        )}
      </Box>
    </Box>
  )
}

// ── Meetings ────────────────────────────────────────────────────────────────────

function formatMeetingTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short', timeZone: 'America/Los_Angeles',
  })
}

function MeetingsTab({ meetings }: { meetings: MeetingItem[] }) {
  const now      = Date.now()
  const upcoming = meetings.filter(m => new Date(m.scheduled_at).getTime() >= now)
  const past     = meetings.filter(m => new Date(m.scheduled_at).getTime() < now).reverse()

  const row = (m: MeetingItem) => (
    <Box
      key={m.id}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Chip
          label={m.kind === 'class' ? 'Class' : 'Mentor'}
          size="small"
          color={m.kind === 'class' ? 'secondary' : 'primary'}
          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{m.title}</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
        {formatMeetingTime(m.scheduled_at)}
        {m.format ? ` · ${m.format === 'in_person' ? 'In person' : m.format === 'online' ? 'Online' : 'Simulcast'}` : ''}
        {m.location && m.format !== 'online' ? ` · ${m.location}` : ''}
      </Typography>
    </Box>
  )

  return (
    <Box sx={{ maxWidth: 620 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Meetings</Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 3, lineHeight: 1.6 }}>
        Scheduled mentor meetings and class meetings for your student.
      </Typography>

      {upcoming.length === 0 && past.length === 0 && (
        <Alert severity="info">No meetings scheduled right now.</Alert>
      )}

      {upcoming.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{
            fontWeight: 700, mb: 1.5, color: 'text.secondary',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.72rem',
          }}>
            Upcoming
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: past.length ? 3 : 0 }}>
            {upcoming.map(row)}
          </Box>
        </>
      )}

      {past.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{
            fontWeight: 700, mb: 1.5, color: 'text.secondary',
            letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.72rem',
          }}>
            Past
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.7 }}>
            {past.slice(0, 5).map(row)}
          </Box>
        </>
      )}
    </Box>
  )
}

// ── Message (parent → mentor) ────────────────────────────────────────────────────

function fmtMsgTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function MessageTab({
  studentFirst, messages, onSend,
}: {
  studentFirst: string
  messages:     ParentMsg[]
  onSend:       (text: string) => Promise<string | null>
}) {
  const [body, setBody] = useState('')

  return (
    <Box sx={{ maxWidth: 620 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Message the mentor</Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.9rem', mb: 3, lineHeight: 1.6 }}>
        Send a question or note about {studentFirst || 'your student'}'s research. The Ardent Scholar
        team will make sure the right person responds — you'll get a reply by email and here.
      </Typography>

      {messages.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {messages.map(m => {
            const mine = m.direction === 'parent_to_staff'
            return (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 1.25 }}>
                <Box sx={{
                  maxWidth: '80%', p: 1.5, borderRadius: 1.5,
                  bgcolor: mine ? 'rgba(15,43,76,0.08)' : 'background.paper',
                  border: '1px solid', borderColor: mine ? 'primary.light' : 'divider',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                    {mine ? 'You' : 'Ardent Scholar'} · {fmtMsgTime(m.created_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, mt: 0.5 }}>
                    {m.body}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      <PolishComposer
        value={body}
        onChange={setBody}
        onSend={onSend}
        placeholder={`Ask a question or share a note about ${studentFirst || 'your student'}…`}
        sendLabel="Send to mentor"
        showPolish={false}
      />
    </Box>
  )
}
